/**
 * useSearchFilterParams
 *
 * The single source of truth for search & filter state in the public app.
 * All state lives in the URL — no local React state for committed values.
 *
 * Contract:
 *   - `q`  param  → search mode   (clears all filter keys on set)
 *   - filter keys → filter mode   (clears `q` on set)
 *   - neither     → default mode  (fetches all data)
 *   - `page`      → current page  (preserved across mode changes when navigating;
 *                                  reset to 1 whenever mode or params change)
 *
 * FilterValue typing:
 *   The hook uses each FilterSection's `type` field to deserialize URL strings
 *   into properly-typed JavaScript values:
 *
 *   | type        | URL             | FilterValue           |
 *   |-------------|-----------------|-----------------------|
 *   | select      | ?k=value        | string | null         |
 *   | multiSelect | ?k=a&k=b        | string[]              |
 *   | boolean     | ?k=true/false   | boolean | null        |
 *   | number      | ?k=42           | number | null         |
 *   | date        | ?k=2025-06-01   | string | null         |
 *   | text        | ?k=term         | string | null         |
 */

import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { FilterSection, FilterState, FilterValue } from '@/types/filter'

// ─── Constants ──────────────────────────────────────────────────────────────

const SEARCH_PARAM = 'q'
const PAGE_PARAM = 'page'
const SIZE_PARAM = "size"

// ─── Types ───────────────────────────────────────────────────────────────────

export type SearchFilterMode = 'search' | 'filter' | 'default'

export interface SearchFilterSnapshot {
  mode: SearchFilterMode
  searchTerm: string
  filterState: FilterState
  page: number
  size: number
  apiFilters: FilterState
}

export interface SearchFilterActions {
  setSearch: (term: string) => void
  setFilters: (state: FilterState) => void
  removeFilter: (key: string, urlValue: string) => void
  clearFilters: () => void
  setPage: (page: number) => void
  setPageSize?: (size: number) => void
  getActiveFilterCount: (sections: FilterSection[]) => number
}

export type UseSearchFilterParamsReturn = SearchFilterSnapshot & SearchFilterActions

// ─── Serialization helpers ────────────────────────────────────────────────────

/**
 * Deserialize URLSearchParams → typed FilterState using section type metadata.
 */
function deserializeFilterState(
  params: URLSearchParams,
  sections: FilterSection[]
): FilterState {
  const state: FilterState = {}

  sections.forEach(section => {
    const type = section.type ?? 'select'

    switch (type) {
      case 'multiSelect':
        state[section.key] = params.getAll(section.key)
        break

      case 'boolean': {
        const raw = params.get(section.key)
        state[section.key] = raw === null ? null : raw === 'true'
        break
      }

      case 'number': {
        const raw = params.get(section.key)
        state[section.key] = raw === null ? null : Number(raw)
        break
      }

      // 'select' | 'date' | 'text' — all single string values
      default:
        state[section.key] = params.get(section.key)
    }
  })

  return state
}

/**
 * Serialize typed FilterState → URLSearchParams.
 * Empty / null values are omitted.
 */
function serializeFilterState(
  state: FilterState,
  sections: FilterSection[]
): URLSearchParams {
  const next = new URLSearchParams()

  sections.forEach(section => {
    const val: FilterValue = state[section.key]
    if (val === null || val === undefined) return

    const type = section.type ?? 'select'

    if (type === 'multiSelect' && Array.isArray(val)) {
      ;(val as string[]).forEach(v => { if (v !== '') next.append(section.key, v) })
    } else if (!Array.isArray(val)) {
      const str = String(val)
      if (str !== '') next.set(section.key, str)
    }
  })

  return next
}

/**
 * A filter state has active values when at least one field is non-empty.
 * Note: boolean `false` IS active — the user explicitly chose "No".
 */
function hasActiveFilters(state: FilterState): boolean {
  return Object.values(state).some(v => {
    if (v === null || v === undefined) return false
    if (Array.isArray(v)) return v.length > 0
    if (typeof v === 'string') return v.length > 0
    return true // boolean (true OR false), non-zero number, etc.
  })
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * @param sections  Stable reference — define as a module-level constant or
 *                  wrap in useMemo inside the calling component.
 */
export function useSearchFilterParams(sections?: FilterSection[]): UseSearchFilterParamsReturn {
  const [searchParams, setSearchParams] = useSearchParams()

  // ── Derived state from URL ─────────────────────────────────────────────────

  const searchTerm = searchParams.get(SEARCH_PARAM) ?? ''
  const page = Math.max(1, Number(searchParams.get(PAGE_PARAM) ?? 1))
  const size = Math.max(1, Number(searchParams.get(SIZE_PARAM) ?? 10))

  const filterState = useMemo(
    () => deserializeFilterState(searchParams, sections ?? []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParams.toString(), sections]
  )

  const mode: SearchFilterMode = searchTerm
    ? 'search'
    : hasActiveFilters(filterState)
    ? 'filter'
    : 'default'

  // ── Actions ────────────────────────────────────────────────────────────────

  const setSearch = useCallback(
    (term: string) => {
      if (!term.trim()) {
        setSearchParams(prev => {
          const next = new URLSearchParams(prev)
          next.delete(SEARCH_PARAM)
          next.delete(PAGE_PARAM)
          next.delete(SIZE_PARAM)
          return next
        })
        return
      }
      const next = new URLSearchParams()
      next.set(SEARCH_PARAM, term.trim())
      setSearchParams(next)
    },
    [setSearchParams]
  )

  const setFilters = useCallback(
    (state: FilterState) => {
      setSearchParams(serializeFilterState(state, sections ?? []))
    },
    [sections, setSearchParams]
  )

  /**
   * Remove one URL string value from a key.
   * Works for all field types:
   *   - multiSelect: removes the specific value, keeps others
   *   - all others: deletes the key entirely (the urlValue is the only value)
   */
  const removeFilter = useCallback(
    (key: string, urlValue: string) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev)
          const remaining = next.getAll(key).filter(v => v !== urlValue)
          next.delete(key)
          remaining.forEach(v => next.append(key, v))
          next.delete(PAGE_PARAM)
          next.delete(SIZE_PARAM)
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const clearFilters = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true })
  }, [setSearchParams])

  const setPage = useCallback(
    (p: number) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          if(p <= 1) {
            next.delete(PAGE_PARAM)
          } else {
            next.set(PAGE_PARAM, String(p))
          }
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const setPageSize = useCallback(
    (s: number) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev)
          if(s <= 1) {
            next.delete(SIZE_PARAM)
          } else {
            next.set(SIZE_PARAM, String(s))
          }
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )


    // Derive API params from url state
  const apiFilters = useMemo(() => {
    const apiFilter: FilterState = {}
    Object.entries(filterState).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        apiFilter[key] = value
      }
    })
    return apiFilter
  }, [filterState])

    const getActiveFilterCount = (sections: FilterSection[] = []): number => {
    return sections.reduce((sum, section) => {
      const value = filterState[section.key]
      if (value === null || value === undefined) return sum
      if (Array.isArray(value)) return sum + value.length
      if (typeof value === 'string') return sum + (value !== '' ? 1 : 0)
      return sum + 1 // boolean (true OR false), non-zero number, etc.
    }, 0)
  }


  return {
    mode,
    searchTerm,
    filterState,
    page,
    setSearch,
    setFilters,
    removeFilter,
    clearFilters,
    setPage,
    size,
    setPageSize,
    apiFilters,
    getActiveFilterCount
  }
}


